<script lang="ts" context="module">
  export const runnerMarketMapping = new Map<string, string>(); // marketId -> runnerId
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import type {
    BetfairMarket,
    BookmakerMarket,
    LayoutPreference,
    MarketOverrounds,
    Race,
    Runner,
    RunnerTopPricesMap
  } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { feedBookmakers, type Bookmaker } from '$utils/bookmakers';
  import {
    MarketMap,
    getBookmakerMarketTypeFromLayoutPreference,
    getVacantBoxes,
    isRaceClosed,
    isVacantBox,
    sortRunnersByPrice
  } from '$utils/race';
  import { activeSiteConfig, defaultLayoutConfigFixedWin } from '$utils/ui';
  import { flip } from 'svelte/animate';
  import { get } from 'svelte/store';
  import BookmakerHeaderRow from './BookmakerHeaderRow.svelte';
  import LayoutToggle from './LayoutToggle.svelte';
  import MarketOverroundRow from './MarketOverroundRow.svelte';
  import PriceGridHeaderRow from './PriceGridHeaderRow.svelte';
  import RunnerGridRow from './RunnerGridRow.svelte';
  import { sortedRunner } from './RunnerInfoCell.svelte';
  import { browser, dev } from '$app/environment';
  import { selectedRace } from '$lib/stores';

  export let race: Race;
  export let marketMapping: MarketMap;
  export let marketOverrounds: MarketOverrounds;
  export let bestPriceOverrounds: {
    fixedWin: number;
    fixedPlace: number;
  };
  export let runnerTopPricesMap: RunnerTopPricesMap;
  export let sortRunners: (() => void) | undefined;

  $: ({ layoutConfigStore } = $page.data);
  $: activeLayoutConfig = layoutConfigStore?.activeLayoutConfig;

  $: bookmakerMarkets =
    race.runners
      .find((r) => !r.scratchedTime)
      ?.bookmakerMarkets?.map((m) => m.bookmaker)
      .filter((b) => {
        if (b === 'Betfair') return false;
        return (
          $activeLayoutConfig?.layoutPreferences?.findIndex((p) => {
            return (
              p.bookmaker === b &&
              p.type === $activeLayoutConfig?.showRemainingBookmakers
            );
          }) === -1
        );
      })
      .sort() ?? [];

  let computedLayoutPreferences =
    $activeLayoutConfig?.layoutPreferences ??
    defaultLayoutConfigFixedWin.layoutPreferences;

  $: {
    if (
      $activeLayoutConfig?.showRemainingBookmakers &&
      $activeLayoutConfig.showRemainingBookmakers !== 'NONE'
    ) {
      // pull out all bookmakers that have markets in the current race
      bookmakerMarkets =
        race.runners
          .find((r) => !r.scratchedTime)
          ?.bookmakerMarkets?.map((m) => m.bookmaker)
          ?.filter((b) => {
            if (b === 'Betfair') return false;
            return (
              $activeLayoutConfig?.layoutPreferences?.findIndex((p) => {
                return (
                  p.bookmaker === b &&
                  p.type === $activeLayoutConfig?.showRemainingBookmakers
                );
              }) === -1
            );
          })
          .sort() ?? [];
    } else {
      bookmakerMarkets = [];
    }

    computedLayoutPreferences =
      $activeLayoutConfig?.layoutPreferences
        .concat(
          // pull all bookmakers from Bookmakers enum
          bookmakerMarkets.map((bookmaker) => {
            return {
              bookmaker: bookmaker,
              locked: false,
              type: $activeLayoutConfig?.showRemainingBookmakers,
              name: 'Remaining'
            } as LayoutPreference;
          })
        )
        .sort((a, b) => {
          // if we are not sorting, keep any statically added preferences at the front
          if (!$sortedRunner) {
            if (a.name !== 'Remaining' && b.name === 'Remaining') return -1;
            if (a.name === 'Remaining' && b.name !== 'Remaining') return 1;
            if (a.name !== 'Remaining' && b.name !== 'Remaining') {
              // return their index in the layout preferences
              return (
                ($activeLayoutConfig?.layoutPreferences?.indexOf(a) ?? 0) -
                  ($activeLayoutConfig?.layoutPreferences?.indexOf(b) ?? 0) ?? 0
              );
            }
          }

          // if any are locked, always keep position
          if (a.locked && !b.locked) return -1;
          if (b.locked && !a.locked) return 1;
          if (a.locked && b.locked) {
            // if both are locked, sort by their index in the layout preferences
            return (
              ($activeLayoutConfig?.layoutPreferences?.indexOf(a) ?? 0) -
                ($activeLayoutConfig?.layoutPreferences?.indexOf(b) ?? 0) ?? 0
            );
          }

          // always push text columns to the back
          if (a.type === 'TEXT') return 1;
          if (b.type === 'TEXT') return -1;

          const aMarketType = getBookmakerMarketTypeFromLayoutPreference(a);
          const bMarketType = getBookmakerMarketTypeFromLayoutPreference(b);
          if (!aMarketType) return -1;
          if (!bMarketType) return 1;

          if (!a.bookmaker) return -1;
          if (!b.bookmaker) return 1;

          // always push fluc graphs to the end
          if (a.type === 'FIXED_WIN_FLUCS' || a.type === 'FIXED_PLACE_FLUCS')
            return 1;
          if (b.type === 'FIXED_WIN_FLUCS' || b.type === 'FIXED_PLACE_FLUCS')
            return -1;

          // get the overrounds for both bookies
          const aOverround = marketOverrounds.get(a.bookmaker)?.[aMarketType];
          const bOverround = marketOverrounds.get(b.bookmaker)?.[bMarketType];

          // if the bookmaker has a < overround, then they should never be sorted
          // if (aOverround && get(aOverround) < 100) return 1;
          // if (bOverround && get(bOverround) < 100) return -1;

          // if the bookmaker has not been updated in a while - push them to the back
          const aLastUpdated = race.links.find(
            (l) => l.bookmaker === a.bookmaker
          )?.lastSuccessfulPriceUpdate;
          const bLastUpdated = race.links.find(
            (l) => l.bookmaker === b.bookmaker
          )?.lastSuccessfulPriceUpdate;

          if (
            (!isRaceClosed(race) &&
              aLastUpdated &&
              !feedBookmakers.includes(a.bookmaker) &&
              aLastUpdated.getTime() <
                new Date().getTime() - 1000 * 60 * 60 * 3) ||
            (isRaceClosed(race) &&
              aLastUpdated &&
              aLastUpdated.getTime() <
                race.startTime.getTime() - 1000 * 60 * 60 * 3 &&
              !feedBookmakers.includes(a.bookmaker))
          )
            return 1;
          if (
            (!isRaceClosed(race) &&
              bLastUpdated &&
              bLastUpdated.getTime() <
                new Date().getTime() - 1000 * 60 * 60 * 3) ||
            (isRaceClosed(race) &&
              bLastUpdated &&
              bLastUpdated.getTime() <
                race.startTime.getTime() - 1000 * 60 * 60 * 3)
          )
            return -1;

          // check if we are sorting by a selection
          if ($isSubscribed && $sortedRunner) {
            // get market ids for the runner
            const runner = race.runners.find(
              (r) => r.id === $sortedRunner?.runnerId
            );
            if (!runner || !runner.bookmakerMarkets) {
              // if we can't find the runner then unset the sorted runner
              sortedRunner.set(undefined);
            } else {
              const aMarketId = runner.bookmakerMarkets.find(
                (m) => m.bookmaker === a.bookmaker
              )?.id;
              const bMarketId = runner.bookmakerMarkets.find(
                (m) => m.bookmaker === b.bookmaker
              )?.id;
              if (aMarketId && bMarketId) {
                // if both bookies have markets for the runner, sort by the selection
                const aPriceStore = marketMapping.getBookmakerMarket(aMarketId);
                const bPriceStore = marketMapping.getBookmakerMarket(bMarketId);
                const aPrice = aPriceStore
                  ? get(aPriceStore)?.[aMarketType]
                  : undefined;
                const bPrice = bPriceStore
                  ? get(bPriceStore)?.[bMarketType]
                  : undefined;

                if (aPrice?.price && bPrice?.price) {
                  if ($sortedRunner.direction === 'asc')
                    return aPrice.price - bPrice.price;
                  else return bPrice.price - aPrice.price;
                } else if (aPrice?.price) {
                  return -1;
                } else if (bPrice?.price) {
                  return 1;
                }
              }
            }
          }

          if (!$isSubscribed) {
            // if the user isn't subscribed - most bookies will have < 100% overrounds
            // as not all markets are available to them,
            // so we will return ordered by the size)
            return (
              (bOverround?.sizeNotZero() ?? 0) -
              (aOverround?.sizeNotZero() ?? 0)
            );
          }

          // if both bookies have overrounds, sort by the overrounds
          if (aOverround && bOverround) {
            const a = get(aOverround);
            const b = get(bOverround);
            // if overround is zero, sort to the back
            if (a === 0) return 1;
            if (b === 0) return -1;
            // otherwise sort by overround
            return a - b;
          } else if (aOverround) {
            return -1;
          } else if (bOverround) {
            return 1;
          }

          // sort the rest alphabetically
          return a.bookmaker.localeCompare(b.bookmaker);
        }) ?? [];
  }

  let runners = race.runners;
  $: {
    // sort runners by price on load and when the layout config changes
    $activeSiteConfig;
    sortRunners?.();
  }
  sortRunners = () => {
    bookmakerIds = $activeLayoutConfig?.runnerPriceSortBookmaker
      ? getAllBookmakerMarketIds($activeLayoutConfig.runnerPriceSortBookmaker)
      : [];

    runners = race.runners
      .concat(
        getVacantBoxes(race).map((box) => {
          return {
            id: 'vacant-' + box,
            name: 'Vacant Box',
            number: box,
            trainerName: '',
            riderName: ''
          } as Runner;
        })
      )
      .filter((r) => {
        if ($activeLayoutConfig?.scratchedRunnerVisibility === 'HIDE') {
          return !r.scratchedTime && !isVacantBox(r);
        } else {
          return true;
        }
      })
      .sort((a, b) => {
        if (
          $activeLayoutConfig?.runnerSort === 'PRICE_ASC' ||
          $activeLayoutConfig?.runnerSort === 'PRICE_DESC'
        ) {
          // if scratched, always push to buttom
          if (a.scratchedTime || isVacantBox(a)) {
            return 1;
          } else if (b.scratchedTime || isVacantBox(b)) {
            return -1;
          }
          return sortRunnersByPrice(
            a,
            b,
            $activeLayoutConfig.runnerPriceSortBookmaker,
            bookmakerIds,
            marketMapping,
            'fixedWin', // only use fixed win for now as place markets are proportional
            $activeLayoutConfig.runnerSort === 'PRICE_DESC'
          );
        }
        if ($activeLayoutConfig?.runnerSort === 'H_BARRIER') {
          // if scratched, always push to buttom
          if (a.scratchedTime || isVacantBox(a)) {
            return 1;
          } else if (b.scratchedTime || isVacantBox(b)) {
            return -1;
          }

          // if barrier is not set, sort by number
          if (!a.barrier || !b.barrier) {
            return a.number - b.number;
          }
          return a.barrier - b.barrier;
        }
        if ($activeLayoutConfig?.runnerSort === 'H_BARRIER_DESC') {
          // if scratched, always push to buttom
          if (a.scratchedTime || isVacantBox(a)) {
            return 1;
          } else if (b.scratchedTime || isVacantBox(b)) {
            return -1;
          }

          // if barrier is not set, sort by number
          if (!a.barrier || !b.barrier) {
            return a.number - b.number;
          }
          return b.barrier - a.barrier;
        }
        if ($activeLayoutConfig?.scratchedRunnerVisibility === 'BOTTOM') {
          // push scratched runners to the bottom
          if (
            (a.scratchedTime || isVacantBox(a)) &&
            (b.scratchedTime || isVacantBox(b))
          ) {
            return a.number - b.number;
          } else if (a.scratchedTime || isVacantBox(a)) {
            return 1;
          } else if (b.scratchedTime || isVacantBox(b)) {
            return -1;
          }
        }
        return a.number - b.number;
      });
  };
  // get the market ids if we are sorting by price
  let bookmakerIds: { marketId: string; runnerId: string }[] = [];
  function getAllBookmakerMarketIds(bookmaker: Bookmaker) {
    // get all market ids from a specific bookmaker
    return race.runners
      .map((r) => {
        let market: BetfairMarket | BookmakerMarket | undefined =
          r.bookmakerMarkets?.find((m) => m.bookmaker === bookmaker);
        if (!market) {
          market = r.betfairMarkets?.find((m) => m.marketName === 'win');
        }
        return { marketId: market?.id, runnerId: r.id };
      })
      .filter((m) => m?.marketId) as { marketId: string; runnerId: string }[];
  }

  // work out which betfair columns are present in this layout
  // used to show the relevant SP column on race with SP
  $: betfairMarketTypes = computedLayoutPreferences
    .filter((p) => p.bookmaker === 'Betfair')
    .map((p) =>
      p.type === 'FIXED_WIN' || p.type === 'FIXED_WIN_FLUCS' ? 'win' : 'place'
    )
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort((a) => (a === 'win' ? -1 : 1));

  let betfairTotalMatchedIds = {
    fixedWin: runners
      .find((r) => r.betfairMarkets?.length)
      ?.betfairMarkets?.find((m) => m.marketName === 'win'),
    fixedPlace: runners
      .find((r) => r.betfairMarkets?.length)
      ?.betfairMarkets?.find((m) => m.marketName === 'place')
  };

  $: betfairTotalMatchedStores = {
    win: betfairTotalMatchedIds.fixedWin?.id
      ? marketMapping.getBetfairMarket(betfairTotalMatchedIds.fixedWin?.id)
      : undefined,
    place: betfairTotalMatchedIds.fixedPlace?.id
      ? marketMapping.getBetfairMarket(betfairTotalMatchedIds.fixedPlace?.id)
      : undefined
  };
</script>

{#if race}
  <div class="relative mb-2">
    <div class="absolute z-[21] md:fixed">
      <LayoutToggle {sortRunners} />
    </div>
    <table class="z-10 table-fixed border-collapse border-gray-200 bg-white">
      <thead class="md:sticky md:top-0 md:z-20">
        <BookmakerHeaderRow {computedLayoutPreferences} {betfairMarketTypes} />
        <PriceGridHeaderRow {computedLayoutPreferences} {betfairMarketTypes} />
      </thead>
      <tbody class="">
        {#each runners as runner (runner.id)}
          <tr class="border border-l-0" animate:flip>
            <RunnerGridRow
              {runner}
              {marketMapping}
              {computedLayoutPreferences}
              {betfairMarketTypes}
              runnerTopPrices={runnerTopPricesMap.get(runner.id)}
            />
          </tr>
        {/each}
        {#if $isSubscribed}
          <tr
            class="text-clip border bg-gray-50/50 text-center text-[10px] italic text-gray-400 shadow-inner"
          >
            <MarketOverroundRow
              {betfairTotalMatchedStores}
              {marketOverrounds}
              {computedLayoutPreferences}
              {betfairMarketTypes}
              {bestPriceOverrounds}
            />
          </tr>
        {/if}
      </tbody>
    </table>
    {#if !$isSubscribed && browser}
      <div class="ml-2 mt-1">
        <span class="text-xs text-gray-700"
          >Without a premium account, prices are delayed and do not update
          automatically without a page refresh</span
        >
      </div>
    {/if}
    {#if dev}
      <div class="ml-2 mt-1">
        <span class="text-xs text-gray-700">{$selectedRace?.id}</span>
      </div>
    {/if}
  </div>
{/if}
